"use client";

import Link from "next/link";
import { useRouter } from "next/navigation";
import { useEffect } from "react";
import { useAuth, signOut, hasRole } from "@/lib/firebase/auth";
import { ProgramProvider, ProgramSwitcher } from "@/lib/firebase/program-context";
import { NavDropdown } from "@/components/NavDropdown";

export default function DashboardLayout({ children }: { children: React.ReactNode }) {
  const router = useRouter();
  const { user, profile, loading } = useAuth();

  useEffect(() => {
    if (!loading && !user) router.replace("/login");
  }, [user, loading, router]);

  if (loading) {
    return <div className="min-h-screen grid place-items-center text-slate-500">กำลังโหลด...</div>;
  }

  if (!user) return null;

  return (
    <ProgramProvider>
    <div className="min-h-screen flex flex-col">
      <header className="border-b bg-white sticky top-0 z-10 print:hidden">
        <div className="mx-auto max-w-7xl px-6 py-3 flex items-center justify-between gap-3">
          <Link href="/dashboard" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-brand-500 text-white grid place-items-center font-bold text-sm">มมส</div>
            <div>
              <div className="text-[10px] text-slate-500 leading-tight">มหาวิทยาลัยมหาสารคาม</div>
              <div className="text-sm font-semibold text-brand-700 leading-tight">ระบบทวนสอบ</div>
            </div>
          </Link>
          <ProgramSwitcher />
          <nav className="flex items-center gap-4">
            {hasRole(profile, "instructor") && (
              <Link href="/dashboard?tab=instructor" className="hover:underline text-sm">รายวิชาของฉัน</Link>
            )}

            {hasRole(profile, "chair") && (
              <NavDropdown
                label="หลักสูตร"
                icon="📖"
                items={[
                  { href: "/dashboard?tab=chair", label: "มอบหมายผู้สอน" },
                  { href: "/curriculum", label: "เล่มหลักสูตร (มคอ.2)" },
                ]}
              />
            )}

            {(hasRole(profile, "chair") || hasRole(profile, "committee")) && (
              <NavDropdown
                label="ทวนสอบ"
                icon="📋"
                items={[
                  ...(hasRole(profile, "chair")
                    ? [{ href: "/verifications/plan", label: "แผนทวนสอบ" }]
                    : []),
                  ...(hasRole(profile, "committee")
                    ? [{ href: "/dashboard?tab=reviewer", label: "งานทวนสอบของฉัน" }]
                    : []),
                ]}
              />
            )}

            {hasRole(profile, "chair") && (
              <NavDropdown
                label="รายงาน"
                icon="📊"
                items={[
                  { href: "/reports/plo-achievement", label: "ผล PLO (ระหว่างเรียน)" },
                  { href: "/reports/plo-exit", label: "ผล PLO (สำเร็จการศึกษา) 🎓" },
                  { href: "/reports/clo-achievement", label: "ผล CLO" },
                  { href: "/reports/curriculum-mapping", label: "Curriculum Map" },
                ]}
              />
            )}

            {hasRole(profile, "chair") && (
              <Link href="/tqf7" className="hover:underline text-sm">📄 มคอ.7</Link>
            )}

            <Link href="/manual" className="hover:underline text-slate-500 text-sm" title="คู่มือการใช้งาน">📘 คู่มือ</Link>

            {hasRole(profile, "chair") && (
              <Link href="/admin/programs" className="text-slate-400 hover:text-slate-600" title="ตั้งค่าหลักสูตร (Admin)">⚙️</Link>
            )}

            <span className="text-slate-600 hidden sm:inline text-sm">{profile?.displayName ?? user.email}</span>
            <button onClick={() => signOut()} className="text-xs rounded border px-2 py-1 hover:bg-slate-50">ออกจากระบบ</button>
          </nav>
        </div>
      </header>
      <main className="flex-1 bg-slate-50 print:bg-white">
        <div className="mx-auto max-w-7xl px-6 py-6 print:p-0 print:max-w-none">{children}</div>
      </main>
      {/* Print rules are defined in src/app/globals.css under @media print */}
    </div>
    </ProgramProvider>
  );
}
